import React, { useState, useEffect, useCallback } from 'react';
import { useLiveQueue } from '../../hooks/useLiveQueue';
import { useNavigate } from 'react-router-dom';
import { workflowAPI } from '../../lib/api';
import { formatDateTime } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Pill, Check, Clock, Package } from 'lucide-react';
import { toast } from 'sonner';

const PharmacyQueue = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [completionNotes, setCompletionNotes] = useState('');

  const fetchQueue = useCallback(async () => {
    try {
      const response = await workflowAPI.getPharmacyQueue();
      setQueue(response.data);
    } catch (error) {
      toast.error('Failed to load pharmacy queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQueue();
    const interval = setInterval(fetchQueue, 30000);
    return () => clearInterval(interval);
  }, [fetchQueue]);

  useLiveQueue('pharmacy', fetchQueue);

  const openCompleteDialog = (item) => {
    setSelectedItem(item);
    setCompletionNotes('');
    setCompleteDialogOpen(true);
  };

  const handleComplete = async () => {
    try {
      await workflowAPI.completeItem(selectedItem.id, { notes: completionNotes });
      toast.success('Prescription dispensed and sent to Reception');
      setCompleteDialogOpen(false);
      fetchQueue();
    } catch (error) {
      toast.error('Failed to complete');
    }
  };

  return (
    <div data-testid="pharmacy-queue-page">
      <header className="page-header glass-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">Pharmacy Queue</h1>
            <p className="text-slate-500">Prescriptions waiting to be dispensed</p>
          </div>
          <Badge className="bg-red-500 text-white text-lg px-4 py-2">
            {queue.length} Pending
          </Badge>
        </div>
      </header>

      <div className="page-content">
        <Card>
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-lg font-semibold font-[Manrope] flex items-center gap-2">
              <Package className="w-5 h-5 text-red-500" />
              Pending Prescriptions
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Loading queue...</div>
            ) : queue.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <Pill className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>No pending prescriptions</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Time</TableHead>
                    <TableHead>Patient</TableHead>
                    <TableHead>Prescribed By</TableHead>
                    <TableHead>Medicines</TableHead>
                    <TableHead>Prescription ID</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {queue.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm">
                          <Clock className="w-3 h-3 text-slate-400" />
                          {formatDateTime(item.created_at)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{item.patient?.full_name || 'Unknown'}</p>
                          <p className="text-xs text-slate-500">{item.patient?.patient_id}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm">{item.referred_by_name}</p>
                          <p className="text-xs text-slate-500 capitalize">{item.referred_by_role?.replace('_', ' ')}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {item.prescription?.medicines?.slice(0, 2).map((med, idx) => (
                            <div key={`${med.medicine_name}-${med.dosage}`} className="text-xs">
                              <Badge variant="outline" className="mr-1">
                                {med.medicine_name}
                              </Badge>
                              <span className="text-slate-500">{med.dosage}</span>
                            </div>
                          ))}
                          {item.prescription?.medicines?.length > 2 && (
                            <Badge variant="outline" className="text-xs">
                              +{item.prescription.medicines.length - 2} more
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {item.prescription?.prescription_id}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          className="bg-green-500 hover:bg-green-600"
                          onClick={() => openCompleteDialog(item)}
                        >
                          <Check className="w-4 h-4 mr-2" />
                          Dispense
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Completion Dialog */}
      <Dialog open={completeDialogOpen} onOpenChange={setCompleteDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">
              Dispense Prescription - {selectedItem?.patient?.full_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm font-medium text-blue-800 mb-2">Medicines to Dispense:</p>
              <div className="space-y-1">
                {selectedItem?.prescription?.medicines?.map((med, idx) => (
                  <div key={`${med.medicine_name}-${med.dosage}`} className="text-xs text-blue-800">
                    • {med.medicine_name} - {med.dosage} ({med.frequency} for {med.duration})
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label>Dispensing Notes (Optional)</Label>
              <Textarea
                value={completionNotes}
                onChange={(e) => setCompletionNotes(e.target.value)}
                placeholder="Any notes about the dispensing (substitutions, stock issues, patient instructions)..."
                rows={4}
              />
            </div>

            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                ✅ Prescription will be marked as <strong>Dispensed</strong>
                <br />
                ✅ Patient will be referred to Reception for discharge
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setCompleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                className="bg-green-500 hover:bg-green-600"
                onClick={handleComplete}
              >
                <Check className="w-4 h-4 mr-2" />
                Mark as Dispensed
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PharmacyQueue;
